// Briques visuelles du Système : fenêtres, pastilles, icônes au trait, navigation.

import { type ReactNode, useEffect, useState } from 'react';

import { api } from './lib/api';
import { VOCAB } from './lib/theme';

export function Fenetre({ children, equerres = false, className = '' }: { children: ReactNode; equerres?: boolean; className?: string }) {
  return (
    <div className={`fenetre ${className}`}>
      {equerres && (
        <>
          <span className="equerre hg" />
          <span className="equerre hd" />
          <span className="equerre bg" />
          <span className="equerre bd" />
        </>
      )}
      {children}
    </div>
  );
}

export function Pastille({ children }: { children: ReactNode }) {
  return <span className="pastille">{children}</span>;
}

/** En-tête des écrans secondaires : retour, pastille, et un élément à droite. */
export function EnTeteRetour({ titre, droite, retour = '#/' }: { titre: string; droite?: ReactNode; retour?: string }) {
  return (
    <div className="entete">
      <a className="carre44" href={retour} aria-label="Retour">
        <Icone nom="retour" />
      </a>
      <Pastille>{titre}</Pastille>
      <span className="carre44 mono muted" style={{ fontSize: 11 }}>{droite}</span>
    </div>
  );
}

type NomIcone = 'bulle' | 'envoyer' | 'plus' | 'hexagone' | 'plume' | 'portail' | 'barres' | 'retour' | 'reglages' | 'coche' | 'suivant' | 'cerveau' | 'atelier' | 'cloche';

export function Icone({ nom, taille = 20 }: { nom: NomIcone; taille?: number }) {
  const props = { width: taille, height: taille, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (nom) {
    case 'bulle':
      return <svg {...props}><path d="M4 5h16v11H9l-5 4z" /><path d="M8 9.5h8M8 12.5h5" /></svg>;
    case 'envoyer':
      return <svg {...props} strokeWidth={2}><path d="M12 19V5M6 11l6-6 6 6" /></svg>;
    case 'plus':
      return <svg {...props} strokeWidth={2}><path d="M12 5v14M5 12h14" /></svg>;
    case 'hexagone':
      return <svg {...props}><path d="M12 2l8.66 5v10L12 22l-8.66-5V7z" /><path d="M12 7l4.33 2.5v5L12 17l-4.33-2.5v-5z" /></svg>;
    case 'plume':
      return <svg {...props}><path d="M14.5 3.5l6 6-11 11H3.5v-6z" /><path d="M12 6l6 6" /></svg>;
    case 'portail':
      return <svg {...props}><ellipse cx="12" cy="12" rx="7" ry="9.5" /><ellipse cx="12" cy="12" rx="3" ry="5" /></svg>;
    case 'barres':
      return <svg {...props}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>;
    case 'retour':
      return <svg {...props} strokeWidth={2}><path d="M15 6l-6 6 6 6" /></svg>;
    case 'suivant':
      return <svg {...props} strokeWidth={2}><path d="M9 6l6 6-6 6" /></svg>;
    case 'coche':
      return <svg {...props} strokeWidth={3}><path d="M5 12l5 5 9-10" /></svg>;
    case 'cerveau':
      return <svg {...props}><path d="M5 4h11l3 3v13H5z" /><path d="M8 10h8M8 14h8M8 18h5" /></svg>;
    case 'atelier':
      return <svg {...props}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9l3 3-3 3M13 15h4" /></svg>;
    case 'cloche':
      return <svg {...props}><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20.5a2 2 0 0 0 4 0" /></svg>;
    case 'reglages':
      return <svg {...props}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" /></svg>;
  }
}

export type Onglet = 'alfred' | 'brief' | 'quetes' | 'portails' | 'cerveau' | 'statut';

/**
 * `sante` : les onglets Quêtes et Statut n'existent que pour un membre qui a le module Santé. Alfred, le chat, vient
 * toujours en premier (décision du 29/10/2026).
 */
export function Nav({ actif, sante = true }: { actif: Onglet; sante?: boolean }) {
  const tous: [Onglet, string, NomIcone, string][] = [
    ['alfred', '#/alfred', 'bulle', VOCAB.nav.alfred],
    ['brief', '#/', 'hexagone', VOCAB.nav.brief],
    ['quetes', '#/quete', 'plume', VOCAB.nav.quetes],
    ['portails', '#/portails', 'portail', VOCAB.nav.portails],
    ['cerveau', '#/cerveau', 'cerveau', VOCAB.nav.cerveau],
    ['statut', '#/statut', 'barres', VOCAB.nav.statut],
  ];
  const onglets = sante ? tous : tous.filter(([id]) => id !== 'quetes' && id !== 'statut');
  return (
    <nav className="nav">
      {onglets.map(([id, href, icone, libelle]) => (
        <a key={id} href={href} aria-current={id === actif ? 'page' : undefined}>
          <Icone nom={icone} />
          {libelle}
        </a>
      ))}
    </nav>
  );
}

/**
 * La cloche des en-têtes (lot 6.2) : les dernières notifications, lues ou non. Le nombre de non lues se relit à
 * l'affichage et au retour de l'app au premier plan.
 */
export function Cloche() {
  const [nonLues, setNonLues] = useState(0);
  useEffect(() => {
    const lire = () => void api.notifications().then((l) => setNonLues(l.filter((n) => !n.lueLe).length)).catch(() => undefined);
    lire();
    const surVisibilite = () => document.visibilityState === 'visible' && lire();
    document.addEventListener('visibilitychange', surVisibilite);
    return () => document.removeEventListener('visibilitychange', surVisibilite);
  }, []);
  return (
    <a className="carre44 cloche" href="#/notifications" aria-label={nonLues ? `Notifications, ${nonLues} non lue(s)` : 'Notifications'}>
      <Icone nom="cloche" />
      {nonLues > 0 && <span className="pastille-compte" aria-hidden="true">{nonLues > 9 ? '9+' : nonLues}</span>}
    </a>
  );
}
